import React from 'react';

/**
 * REQ-106 answer references: `{{question_key}}` or `{{question_key|fallback}}` in
 * a question's header, text or subtext shows the learner's own earlier answer.
 * Keys and the fallback grammar match the backend parser exactly.
 */
// The `u` flag counts fallback length in code points, as Python does.
export const ANSWER_REFERENCE_PATTERN = /^\{\{[ \t]*([A-Za-z0-9][A-Za-z0-9_.:-]{0,254})[ \t]*(?:\|([^{}|\n\r]{0,120}))?\}\}/u;

/** Fallbacks trim only spaces and tabs, identically to the backend parser. */
export const trimAnswerReferenceFallback = (value: string): string => value.replace(/^[ \t]+|[ \t]+$/g, '');

/** Optional wording never holds a question longer than this. */
export const ANSWER_REFERENCE_TIMEOUT_MS = 8000;

export const ANSWER_REFERENCE_NEUTRAL_TEXT = 'your earlier answer';
export const ANSWER_REFERENCE_MISSING_TEXT = 'no answer yet';

export interface AnswerReferenceValue {
  status: 'answered' | 'missing';
  text?: string;
  items?: string[];
  question_type?: string;
  truncated?: boolean;
}

export type AnswerReferenceMap = Record<string, AnswerReferenceValue>;

export interface AnswerReferenceContextValue {
  /** `runtime` shows resolved learner values; `preview` shows the referenced key to authors. */
  mode: 'runtime' | 'preview';
  references: AnswerReferenceMap;
  /** Server digest of the values shown; follow-up evaluation sends it back. */
  digest?: string;
  /** Re-read the values (for example after the server reports they changed). */
  refresh?: () => void;
}

/** Without a provider, references never expose syntax and never claim an answer is missing. */
export const AnswerReferenceContext = React.createContext<AnswerReferenceContextValue | null>(null);

export const ANSWER_REFERENCE_PREVIEW: AnswerReferenceContextValue = { mode: 'preview', references: {} };

export const answerReferencePlainText = (
  key: string,
  fallback: string | null,
  context: AnswerReferenceContextValue | null,
): string => {
  if (context?.mode === 'preview') return `answer: ${key}`;
  const value = context?.references[key];
  if (value?.status === 'answered' && value.text) return value.text;
  if (fallback !== null) return fallback;
  return value ? ANSWER_REFERENCE_MISSING_TEXT : ANSWER_REFERENCE_NEUTRAL_TEXT;
};
